import { useState, useEffect, useRef } from "react";
import { Row, Col, Form, Card } from "react-bootstrap";
import { BsPencil, BsCheck, BsX } from "react-icons/bs";
import axios from "axios";

const EmployeeView = ({ data, EmployeeId, setEmployeeDetails }) => {
    const [editedData, setEditedData] = useState(data);
    const [industries, setIndustries] = useState([]);
    const [countries, setCountries] = useState([]);
    const [editField, setEditField] = useState(null);
    const didFetchRef = useRef(false);
    const [currency, setCurrency] = useState('AED');
    
    useEffect(() => {
        if (!didFetchRef.current) {
            fetchIndustries();
            fetchCountries();
            didFetchRef.current = true;
        }
    }, []);

    const fetchIndustries = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const token = JSON.parse(localStorage.getItem("user-info"))?.token;
            const origin = localStorage.getItem('user-origin');

            const response = await axios.get(`${backendUrl}/api/industries`, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });
            setIndustries(response.data.industry);
        } catch (error) {
            console.error("Error fetching industries:", error);
        }
    };

    const fetchCountries = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const token = JSON.parse(localStorage.getItem("user-info"))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios.get(`${backendUrl}/api/countries`, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });
            const allCountries = response.data.Data;

            const sortedCountries = allCountries.sort((a, b) => {
                if (a.name === 'United Arab Emirates') return -1;
                if (b.name === 'United Arab Emirates') return 1;
                return a.name.localeCompare(b.name);
            });
    
            setCountries(sortedCountries);
            if (!editedData.country_id) {
                const uae = allCountries.find(c => c.name === 'United Arab Emirates');
                if (uae) {
                    setEditedData(prev => ({
                        ...prev,
                        country_id: uae.id,
                        currency: 'AED'
                    }));
                }
            }
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
    };
    
    const handleSave = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const token = JSON.parse(localStorage.getItem("user-info"))?.token;
            const origin = localStorage.getItem('user-origin');
    
            const response = await axios.put(`${backendUrl}/api/employee/${EmployeeId}`, editedData, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });
    
            if (response.status === 200) {
                setEmployeeDetails((prev) => ({ ...prev, ...editedData }));
                setEditField(null);
            }
        } catch (error) {
            console.error("Error saving Employee details:", error);
        }
    };
    
    const handleChange = (e) => {
        const { name, value } = e.target;
    
        setEditedData((prevData) => {
            let updatedData = { ...prevData, [name]: value };
    
            if (name === "country_id") {
                const selectedCountry = countries.find(c => String(c.id) === value);
                updatedData.currency = selectedCountry?.name === "United Arab Emirates" ? "AED" : "USD";
            }
            
            return updatedData;
        });
    };
    
    useEffect(() => {
        if (data && data.id !== editedData.id) {
            setEditedData(data);
        }
    }, [data]);
    
    const getIndustryName = (id) => {
        if (!id) return "--";
        const industry = industries.find((ind) => Number(ind.id) === Number(id));
        return industry ? industry.name : "--";
    };
    
    const getCountryName = (id) => {
        if (!id || countries.length === 0) return "--";
        const country = countries.find((coun) => Number(coun.id) === Number(id));
        return country ? country.name : "--";
    };
    
    // Define fields by section
    const personalInfoFields = [
        { name: "employee_code", label: "Employee Code", type: "text" },
        { name: "first_name", label: "First Name", type: "text" },
        
        { name: "dob", label: "Date of Birth", type: "date" },
        
        { name: "personal_email", label: "Email", type: "email" },
        { name: "personal_mobile", label: "Mobile", type: "text" },
                { name: "nationality", label: "Nationality", type: "text" },
                { name: "address", label: "Address", type: "text" },
                { name: "employee_category", label: "Employee Category", type: "text" },



    ];

    const emergencyContactFields = [
        { name: "emergency_contact_name", label: "Emergency Contact Name", type: "text" },
        { name: "emergency_contact_relation", label: "Relation", type: "text" },
        { name: "emergency_contact_phone", label: "Emergency Contact Phone", type: "text" },
    ];

    const jobDetailFields = [
        { name: "job_title", label: "Job Title", type: "text" },
        { name: "department", label: "Department", type: "text" },
        { name: "reporting_manager", label: "Reporting Manager", type: "text" },
        { name: "work_location", label: "Work Location", type: "text" },
        { name: "join_date", label: "Join Date", type: "date" },
        { name: "probation_end", label: "Probation End", type: "date" },
        { name: "confirmation_date", label: "Confirmation Date", type: "date" },
        { name: "employment_type", label: "Employment Type", type: "select", options: [
            { value: "", label: "Select Type" },
            { value: "Full-Time", label: "Full-Time" },
            { value: "Part-Time", label: "Part-Time" },
            { value: "Contract", label: "Contract" },
            { value: "Intern", label: "Intern" }
        ]},
    ];

    // const additionalFields = [
    //     { name: "address", label: "Address", type: "textarea" },
    //     { name: "country_id", label: "Country", type: "select", options: countries.map((coun) => ({ value: coun.id, label: coun.name })), format: getCountryName },
    //     { name: "org_type", label: "Type", type: "select", options: [
    //         { value: "end customer", label: "End Customer" }, 
    //         { value: "partner", label: "Partner" }
    //     ]},
    //     { name: "currency", label: "Currency", type: "text", readOnly: true },
    // ];

    const renderField = (field, index) => (
        <Col md={6} key={index} className="mb-3 d-flex align-items-center">
            <strong className="view-label me-2" style={{ minWidth: "160px" }}>
                {field.label}:
            </strong>
            <div className="editable-container d-flex align-items-center flex-grow-1">
                {editField === field.name ? (
                    <div className="d-flex align-items-center w-100">
                        {field.type === "select" ? (
                            <Form.Select
                                value={editedData[field.name] || ""}
                                onChange={handleChange}
                                autoFocus
                                className="form-control form-field"
                                name={field.name}
                            >
                                {field.options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        ) : field.type === "textarea" ? (
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={editedData[field.name] || ""}
                                onChange={handleChange}
                                autoFocus
                                className="custom-textarea form-field"
                                name={field.name}
                            />
                        ) : field.type === "date" ? (
                            <Form.Control
                                type="date"
                                name={field.name}
                                value={editedData[field.name] || ""}
                                onChange={handleChange}
                                autoFocus
                                className="form-control form-field"
                            />
                        ) : (
                            <Form.Control
                                type={field.type}
                                name={field.name}
                                value={editedData[field.name] || ""}
                                onChange={handleChange}
                                autoFocus
                                className="form-control form-field"
                                readOnly={field.readOnly}
                            />
                        )}
                        <BsCheck className="text-success mx-2" onClick={handleSave} style={{ cursor: "pointer" }} />
                        <BsX className="text-danger" onClick={() => setEditField(null)} style={{ cursor: "pointer" }} />
                    </div>
                ) : (
                    <div 
                        className="hover1 " 
                        onClick={() => !field.readOnly && setEditField(field.name)}
                    >
                        <span className="hover-field1">
                            {field.format 
                                ? field.format(editedData[field.name])
                                : editedData[field.name] || "--"}
                        </span>
                        {!field.readOnly && <BsPencil className="text-primary pencil-icon1" />}
                    </div>
                )}
            </div>
        </Col>
    );

    return (
        <div className="p-3 font-size-13">
            {/* Personal Information Section */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0 text-primary">Personal Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        {personalInfoFields.map((field, index) => renderField(field, index))}
                    </Row>
                </Card.Body>
            </Card>

            {/* Emergency Contacts Section */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0 text-primary">Emergency Contacts</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        {emergencyContactFields.map((field, index) => renderField(field, index))}
                    </Row>
                </Card.Body>
            </Card>

            {/* Job Details Section */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0 text-primary">Job Details</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        {jobDetailFields.map((field, index) => renderField(field, index))}
                    </Row>
                </Card.Body>
            </Card>

            {/* Additional Information Section */}
            {/* <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0 text-primary">Additional Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        {additionalFields.map((field, index) => renderField(field, index))}
                    </Row>
                </Card.Body>
            </Card> */}
        </div>
    );
};

export default EmployeeView;
